<template>
  <section class="contract-view">
    <div v-if="!isMobile" class="banner">
      <div class="width banner-text">
        <h1>{{ t('82528') }}</h1>
        <h2>{{ t('82968') }}</h2>
      </div>
      <div class="width banner-list">
        <div v-for="item in bannerList" :key="item.title" class="item">
          <CommonImage :src="item.icon" :alt="item.title" loading="eager" />
          <div class="item-text">
            <p>{{ item.title }}</p>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="tab-box">
      <div ref="tabListRef" :class="['width tab-list', { 'is-overflowing': isTabOverflowing }]">
        <button
          v-for="item in tabData"
          :key="item.code"
          type="button"
          :class="['tab-item', { active: tabIndex === item.code }]"
          @click="changeTab(item.code)"
        >
          {{ item.name }}
        </button>
      </div>
    </div>

    <div class="width content">
      <div v-if="!isMobile" class="title">{{ t('82981') }}</div>

      <el-form ref="formRef" :model="form" :rules="rules" :validate-on-rule-change="false" class="contract-form">
        <div v-if="tabIndex !== 4" class="companyName-box">
          <el-form-item prop="lastName" :label="t('82982')">
            <el-input v-model.trim="form.lastName" :placeholder="t('82983')" />
          </el-form-item>

          <el-form-item prop="companyName" :label="t('82984') + '?'">
            <div class="company-autocomplete-wrap">
              <el-autocomplete
                v-model.trim="form.companyName"
                class="companyName-autocomplete"
                popper-class="contract-autocomplete-popper"
                :fetch-suggestions="querySearch"
                :placeholder="t('82985')"
                :trigger-on-focus="false"
                :teleported="false"
                value-key="value"
                @select="handleSelect"
              >
                <template #default="{ item }">
                  <div class="autocomplete-item">
                    <CommonImage
                      class="icon"
                      :src="item.ico || defaultCompanyIcon"
                      :alt="item.value"
                      object-fit="cover"
                      loading="eager"
                    />
                    <div class="name">{{ item.value }}</div>
                    <el-tag v-if="item.type === 1 && !isMobile" size="small" effect="plain">{{ t('82491') }}</el-tag>
                    <el-tag v-if="item.type === 2 && !isMobile" size="small" effect="plain">{{ t('83697') }}</el-tag>
                    <el-tag v-if="item.type === 3 && !isMobile" size="small" effect="plain">{{ t('82566') }}</el-tag>
                    <el-tag v-if="item.type === 4 && !isMobile" size="small" effect="plain">{{ t('83698') }}</el-tag>
                  </div>
                </template>
              </el-autocomplete>
              <!-- <span class="autocomplete-icon">⌕</span> -->
              <CommonImage
                class="autocomplete-icon"
                loading="eager"
                src="https://resources1.interface003.com/web30/2026/06/11/aa4a5ea9-8c56-4670-916d-8e8d29dd9e00.png"
              />
            </div>
          </el-form-item>

          <el-form-item prop="mailbox" :label="t('82986')">
            <el-input v-model.trim="form.mailbox" :placeholder="t('82987')" />
          </el-form-item>
        </div>

        <div v-else class="companyName-box">
          <el-form-item prop="lastName" :label="t('82982')">
            <el-input v-model.trim="form.lastName" :placeholder="t('82983')" />
          </el-form-item>

          <el-form-item prop="nationality" :label="t('83716')" class="nationality-box">
            <div ref="nationTriggerRef" class="selector-trigger" @click.stop="showNationCode = !showNationCode">
              <CommonImage :src="nationObj.flag" :alt="nationObj.name" loading="eager" class="selector-flag" />
              <span class="selector-name">{{ nationObj.name }}</span>
              <span :class="['selector-arrow', { open: showNationCode }]">▾</span>
            </div>
            <div v-if="showNationCode" ref="nationPanelRef" class="selector-panel">
              <div class="selector-search">
                <input v-model.trim="nationKeyword" type="text" :placeholder="t('82927')" />
              </div>
              <div class="selector-results">
                <template v-if="nationKeyword">
                  <button
                    v-for="item in filteredCountries"
                    :key="`nation-${item.countryCode}-${item.code}`"
                    type="button"
                    class="selector-option"
                    @click="selectNation(item)"
                  >
                    <CommonImage :src="item.flag" :alt="item.name" loading="eager" class="selector-flag" />
                    <span>{{ item.name }}</span>
                  </button>
                  <div v-if="!filteredCountries.length" class="selector-empty">{{ t('83714') }}</div>
                </template>
                <template v-else>
                  <div v-for="group in areaGroups" :key="group.name" class="selector-group">
                    <div class="selector-group-title">{{ group.name }}</div>
                    <button
                      v-for="item in group.data"
                      :key="`nation-${item.countryCode}-${item.code}`"
                      type="button"
                      class="selector-option"
                      @click="selectNation(item)"
                    >
                      <CommonImage :src="item.flag" :alt="item.name" loading="eager" class="selector-flag" />
                      <span>{{ item.name }}</span>
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </el-form-item>

          <el-form-item prop="TRC20" :label="t('83717')">
            <el-input v-model.trim="form.TRC20" :placeholder="t('83696')" />
          </el-form-item>
        </div>

        <div class="other-contact">
          <div class="other-contact-title">{{ t('82988') }}</div>

          <div v-if="!isMobile" class="box">
            <div v-if="tabIndex !== 4" ref="itemPhoneRef" class="item">
              <div class="name mb-[16px]">
                <CommonImage :src="resource('/images/contract/phone.svg')" :alt="t('82907')" loading="eager" />
                <span>{{ t('82907') }}</span>
              </div>
              <div class="phone-num-box">
                <div ref="areaTriggerRef" class="left flex-center" @click.stop="showAreaCode = !showAreaCode">
                  <CommonImage :src="phoneObj.flag" :alt="phoneObj.name" loading="eager" class="selector-flag" />
                  <span>{{ phoneObj.code }}</span>
                  <span :class="['selector-arrow', { open: showAreaCode }]">▾</span>
                </div>
                <div v-if="showAreaCode" ref="areaPanelRef" class="selector-panel area-panel">
                  <div class="selector-search">
                    <input v-model.trim="areaKeyword" type="text" :placeholder="t('82927')" />
                  </div>
                  <div class="selector-results">
                    <template v-if="areaKeyword">
                      <button
                        v-for="item in filteredCountries"
                        :key="`area-${item.countryCode}-${item.code}`"
                        type="button"
                        class="selector-option"
                        @click="selectPhoneArea(item)"
                      >
                        <CommonImage :src="item.flag" :alt="item.name" loading="eager" class="selector-flag" />
                        <span class="selector-code">{{ item.code }}</span>
                        <span>{{ item.name }}</span>
                      </button>
                      <div v-if="!filteredCountries.length" class="selector-empty">{{ t('83714') }}</div>
                    </template>
                    <template v-else>
                      <div v-for="group in areaGroups" :key="group.name" class="selector-group">
                        <div class="selector-group-title">{{ group.name }}</div>
                        <button
                          v-for="item in group.data"
                          :key="`area-${item.countryCode}-${item.code}`"
                          type="button"
                          class="selector-option"
                          @click="selectPhoneArea(item)"
                        >
                          <CommonImage :src="item.flag" :alt="item.name" loading="eager" class="selector-flag" />
                          <span class="selector-code">{{ item.code }}</span>
                          <span>{{ item.name }}</span>
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
                <input v-model.trim="form.phone" type="number" :placeholder="t('82989')" />
              </div>
              <div v-if="isErrText" class="errer">{{ errText }}</div>
            </div>

            <div v-if="tabIndex !== 4" class="item">
              <el-form-item>
                <template #label>
                  <div class="name">
                    <CommonImage :src="resource('/images/contract/weChat.svg')" :alt="t('82990')" loading="eager" />
                    <span>{{ t('82990') }}</span>
                  </div>
                </template>
                <el-input v-model.trim="form.weChat" :placeholder="t('82991')" />
              </el-form-item>
            </div>

            <div v-if="tabIndex !== 4" class="item">
              <el-form-item>
                <template #label>
                  <div class="name">
                    <CommonImage :src="resource('/images/contract/whatsApp.svg')" alt="WhatsApp" loading="eager" />
                    <span>WhatsApp</span>
                  </div>
                </template>
                <el-input v-model.trim="form.whatsApp" :placeholder="t('82992')" />
              </el-form-item>
            </div>

            <div v-if="tabIndex === 4" ref="itemEmailRef" class="item">
              <el-form-item>
                <template #label>
                  <div class="name">
                    <CommonImage :src="resource('/images/contract/email.svg')" :alt="t('82986')" loading="eager" />
                    <span>{{ t('82986') }}</span>
                  </div>
                </template>
                <el-input v-model.trim="form.email" :placeholder="t('82987')" />
              </el-form-item>
              <div v-if="isErrText" class="errer">{{ errText }}</div>
            </div>

            <div v-if="tabIndex === 4" class="item">
              <el-form-item>
                <template #label>
                  <div class="name">
                    <CommonImage :src="resource('/images/contract/discord.svg')" alt="Discord" loading="eager" />
                    <span>Discord</span>
                  </div>
                </template>
                <el-input v-model.trim="form.discord" :placeholder="t('83700')" />
              </el-form-item>
            </div>

            <div v-if="tabIndex === 4" class="item">
              <el-form-item>
                <template #label>
                  <div class="name">
                    <CommonImage :src="resource('/images/contract/telegram.svg')" alt="Telegram" loading="eager" />
                    <span>Telegram</span>
                  </div>
                </template>
                <el-input v-model.trim="form.telegram" :placeholder="t('83699')" />
              </el-form-item>
            </div>

            <div class="item">
              <el-form-item class="other-name-item">
                <template #label>
                  <div class="name">
                    <CommonImage :src="resource('/images/contract/other.svg')" :alt="t('82993')" loading="eager" />
                    <span>{{ t('82993') }}</span>
                  </div>
                </template>
                <el-input v-model.trim="form.otherName" :placeholder="t('82994')" />
              </el-form-item>
              <el-form-item>
                <el-input v-model.trim="form.otherNumber" :placeholder="t('82995')" />
              </el-form-item>
            </div>
          </div>

          <div v-else class="box-h5">
            <div class="touch-list">
              <button
                v-for="(item, index) in mobileContactList"
                :key="item.id"
                type="button"
                :class="['item-liaison', { 'item-selected': touchIdx === index }]"
                @click="touchIdx = index"
              >
                <CommonImage :src="item.icon" :alt="item.name" loading="eager" class="ico-liaison" />
                <p class="liaison-txt">{{ item.name }}</p>
              </button>
            </div>

            <template v-if="tabIndex !== 4">
              <div v-show="touchIdx === 0" class="corporation-box">
                <div class="input-zone">
                  <div ref="areaTriggerRef" class="diallin-code" @click.stop="showAreaCode = !showAreaCode">
                    <CommonImage :src="phoneObj.flag" :alt="phoneObj.name" loading="eager" class="selector-flag" />
                    <span class="area-val">{{ phoneObj.code }}</span>
                    <span class="selector-arrow">▾</span>
                  </div>
                  <input v-model.trim="form.phone" type="number" :placeholder="t('82989')" class="corporation-value" />
                </div>
                <div v-if="showAreaCode" ref="areaPanelRef" class="selector-panel mobile-selector-panel">
                  <div class="selector-search">
                    <input v-model.trim="areaKeyword" type="text" :placeholder="t('82927')" />
                  </div>
                  <div class="selector-results">
                    <template v-if="areaKeyword">
                      <button
                        v-for="item in filteredCountries"
                        :key="`mobile-area-${item.countryCode}-${item.code}`"
                        type="button"
                        class="selector-option"
                        @click="selectPhoneArea(item)"
                      >
                        <CommonImage :src="item.flag" :alt="item.name" loading="eager" class="selector-flag" />
                        <span class="selector-code">{{ item.code }}</span>
                        <span>{{ item.name }}</span>
                      </button>
                      <div v-if="!filteredCountries.length" class="selector-empty">{{ t('83714') }}</div>
                    </template>
                    <template v-else>
                      <div v-for="group in areaGroups" :key="group.name" class="selector-group">
                        <div class="selector-group-title">{{ group.name }}</div>
                        <button
                          v-for="item in group.data"
                          :key="`mobile-area-${item.countryCode}-${item.code}`"
                          type="button"
                          class="selector-option"
                          @click="selectPhoneArea(item)"
                        >
                          <CommonImage :src="item.flag" :alt="item.name" loading="eager" class="selector-flag" />
                          <span class="selector-code">{{ item.code }}</span>
                          <span>{{ item.name }}</span>
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <div v-show="touchIdx === 1" class="corporation-box">
                <el-input v-model.trim="form.weChat" :placeholder="t('82991')" />
              </div>

              <div v-show="touchIdx === 2" class="corporation-box">
                <el-input v-model.trim="form.whatsApp" :placeholder="t('82992')" />
              </div>
            </template>

            <template v-else>
              <div v-show="touchIdx === 0" class="corporation-box">
                <el-input v-model.trim="form.email" :placeholder="t('82987')" />
              </div>
              <div v-show="touchIdx === 1" class="corporation-box">
                <el-input v-model.trim="form.discord" :placeholder="t('83700')" />
              </div>
              <div v-show="touchIdx === 2" class="corporation-box">
                <el-input v-model.trim="form.telegram" :placeholder="t('83699')" />
              </div>
            </template>

            <div v-show="touchIdx === 3">
              <el-form-item class="other-name-item">
                <el-input v-model.trim="form.otherName" :placeholder="t('82994')" />
              </el-form-item>
              <el-form-item>
                <el-input v-model.trim="form.otherNumber" :placeholder="t('82995')" />
              </el-form-item>
            </div>
          </div>
        </div>

        <div v-if="tabIndex === 2" class="companyName-box">
          <el-form-item prop="officialWebsite" :label="t('83001')">
            <el-input v-model.trim="form.officialWebsite" :placeholder="t('83002')" />
          </el-form-item>
        </div>

        <div class="contract-textarea">
          <div :class="['text', { 'is-need': tabIndex > 3 }]">{{ introLabel }}</div>
          <el-form-item prop="introduction">
            <el-input
              v-model="form.introduction"
              type="textarea"
              :rows="6"
              maxlength="500"
              show-word-limit
              :placeholder="t('82997')"
            />
          </el-form-item>
        </div>

        <div v-if="tabIndex !== 4" class="upload-box">
          <div class="upload-box-name">{{ t('82998') }}（{{ imgArr.length }}/5）</div>
          <div class="upload-imgarr">
            <div v-for="(item, index) in imgArr" :key="`${item.ext}-${index}`" class="upload-box-imgbox">
              <CommonImage :src="item.base64" :alt="t('83701')" />
              <button type="button" class="delete-btn" @click="deleteImgArr(index)">×</button>
            </div>
            <label v-if="imgArr.length < 5" class="file-box">
              <!-- <span class="camera-icon">📷</span> -->
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/06/11/8df2fa3b-a4c3-4237-b70f-4188aaf57e61.png"
                :alt="t('83701')"
                class="camera-icon"
              />
              <input type="file" accept=".png,.jpg,.jpeg" class="kyc-passin" @change="uploadPhoto" />
            </label>
            <div :class="['text', { updateErr: isUpdateErr }]">{{ updateText }}</div>
          </div>
        </div>

        <button type="button" class="submit-btn" :class="{ disabled: clickable || submitting }" @click="submitForm">
          {{ submitting ? t('83713') : t('83004') }}
        </button>
      </el-form>
    </div>

    <el-dialog v-model="dialogVisible" class="contract-dialog" width="534px" :show-close="false" align-center>
      <template #header>
        <div class="dialog-title">
          <span :class="['title-icon', tipObj.type ? 'title-icon-success' : 'title-icon-error']"></span>
          <span class="title-text">{{ tipObj.type ? t('83715') : t('83712') }}</span>
        </div>
      </template>
      <div class="dialog-content">{{ tipObj.content }}</div>
      <template #footer>
        <div class="dialog-footer">
          <button type="button" class="dialog-button" @click="dialogVisible = false">{{ t('83711') }}</button>
        </div>
      </template>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import { onBeforeRouteLeave } from 'vue-router'
import { request } from '~/api'

const { t, locale } = useAppI18n()

definePageMeta({
  name: 'contact',
  layout: 'default',
})

const contractPageTitle = await getSeoLocaleMessage(locale, '84150')

useHead(() => ({
  title: contractPageTitle,
  meta: [
    {
      name: 'description',
      content: t('84117'),
    },
  ],
}))

type AreaCountry = {
  countryCode: string
  twoCharCode: string
  code: string
  name: string
  flag: string
  'flag-c'?: string
}

type ContactForm = {
  companyCode: string
  companyName: string
  mailbox: string
  phone: string
  weChat: string
  whatsApp: string
  otherNumber: string
  otherName: string
  officialWebsite: string
  lastName: string
  nationality: string
  email: string
  TRC20: string
  discord: string
  telegram: string
  introduction: string
}

type CompanySearchSuggestion = {
  code: string
  value: string
  shortName: string
  ico: string
  type?: number
}

const route = useRoute()
const appStore = useAppStore()
const { invokeV3 } = useLoginApi()
const matomo = useMatomo()

const resourceBase = 'https://resource.tech002.com'
const wikibitSearchGateway = `https://api-gw.${TECH002}`
const defaultCompanyIcon = `${resourceBase}/images/exchanges/eagle.png?v=1`

const resource = (path: string) => `${resourceBase}${path}`

const bannerList = [
  { icon: resource('/images/contract/icon-1.png'), title: t('82969'), desc: t('82970') },
  { icon: resource('/images/contract/icon-2.png'), title: t('82971'), desc: t('82972') },
  { icon: resource('/images/contract/icon-4.png'), title: t('82973'), desc: t('82974') },
  { icon: resource('/images/contract/icon-3.png'), title: t('82975'), desc: t('82976') },
]

const tabData = [
  { name: t('82977'), code: 0 },
  { name: t('82978'), code: 1 },
  { name: t('82979'), code: 2 },
  { name: t('82980'), code: 3 },
]
const CONTACT_TAB_TRACK_TYPE_MAP: Record<number, string> = {
  0: '商务合作',
  1: '牌照更新',
  2: '申请收录',
  3: '投诉建议',
}

// 兼容 Nuxt query 可能出现的数组值，统一取第一个参数。
function normalizeQueryValue(value: unknown) {
  return Array.isArray(value) ? value[0] : value
}

// 将 URL 中 type=1~4 映射为页面内部 tab code=0~3，默认选中商务合作。
function resolveRouteTabIndex(typeQuery: unknown, legacyTabindexQuery?: unknown) {
  const type = Number.parseInt(String(normalizeQueryValue(typeQuery) || ''), 10)
  if (type >= 1 && type <= tabData.length) {
    return type - 1
  }

  const legacyTabindex = Number.parseInt(String(normalizeQueryValue(legacyTabindexQuery) || ''), 10)
  if (legacyTabindex >= 0 && legacyTabindex < tabData.length) {
    return legacyTabindex
  }

  return 0
}

const formRef = ref<FormInstance>()
const tabListRef = ref<HTMLElement>()
const tabIndex = ref(resolveRouteTabIndex(route.query.type, route.query.tabindex))
const touchIdx = ref(0)
const submitting = ref(false)
const dialogVisible = ref(false)
const tipObj = reactive({ type: 1, content: '' })
const isErrText = ref(false)
const errText = ref(t('82995'))
const isUpdateErr = ref(false)
const updateText = ref(t('82999'))
const imgArr = ref<Array<{ base64: string; ext: string }>>([])
const isMobile = ref(false)
const isTabOverflowing = ref(false)
const showAreaCode = ref(false)
const showNationCode = ref(false)
const areaKeyword = ref('')
const nationKeyword = ref('')
const { countries: areaGroups, flatCountries } = useLoginCountries()

const form = reactive<ContactForm>({
  companyCode: '',
  companyName: '',
  mailbox: '',
  phone: '',
  weChat: '',
  whatsApp: '',
  otherNumber: '',
  otherName: '',
  officialWebsite: '',
  lastName: '',
  nationality: '',
  email: '',
  TRC20: '',
  discord: '',
  telegram: '',
  introduction: '',
})

function resolveCountry(country?: AreaCountry | null) {
  return (
    flatCountries.value.find((item) => {
      return (
        item.countryCode === country?.countryCode ||
        item.twoCharCode === country?.twoCharCode ||
        item.code === country?.code
      )
    }) ||
    flatCountries.value.find((item) => item.countryCode === '156') ||
    flatCountries.value[0]
  )
}

const fallbackCountry = resolveCountry()

const phoneObj = reactive<AreaCountry>({
  ...(resolveCountry(appStore.usergetCountry as unknown as AreaCountry) || fallbackCountry),
})

const nationObj = reactive<AreaCountry>({
  ...(resolveCountry(appStore.usergetCountry as unknown as AreaCountry) || fallbackCountry),
})

form.nationality = nationObj.name

const mobileContactList = computed(() => {
  if (tabIndex.value === 4) {
    return [
      { id: '1', icon: resource('/images/contract/email.svg'), name: t('82438') },
      { id: '2', icon: resource('/images/contract/discord.svg'), name: 'Discord' },
      { id: '3', icon: resource('/images/contract/telegram.svg'), name: 'Telegram' },
      { id: '4', icon: resource('/images/contract/other.svg'), name: t('82993') },
    ]
  }

  return [
    { id: '1', icon: resource('/images/contract/phone.svg'), name: t('82907') },
    { id: '2', icon: resource('/images/contract/weChat.svg'), name: t('82990') },
    { id: '3', icon: resource('/images/contract/whatsApp.svg'), name: 'WhatsApp' },
    { id: '4', icon: resource('/images/contract/other.svg'), name: t('82993') },
  ]
})

const introLabel = computed(() => {
  if (tabIndex.value === 0) return `${t('82996')}?`
  if (tabIndex.value === 1) return t('83000')
  if (tabIndex.value === 2) return t('83003')
  if (tabIndex.value === 3) return t('83005')
  return t('83702')
})

const filteredCountries = computed(() => {
  const keyword = (showNationCode.value ? nationKeyword.value : areaKeyword.value).trim().toLowerCase()
  if (!keyword) return []
  return flatCountries.value.filter((item) => {
    return (
      item.name.toLowerCase().includes(keyword) ||
      item.code.toLowerCase().includes(keyword) ||
      item.twoCharCode.toLowerCase().includes(keyword)
    )
  })
})

const clickable = computed(() => {
  if (!isMobile.value) return false

  if (tabIndex.value === 0 || tabIndex.value === 1 || tabIndex.value === 3) {
    return (
      !form.lastName ||
      !form.companyCode ||
      !form.companyName ||
      !form.mailbox ||
      (!form.phone && !form.weChat && !form.whatsApp && (!form.otherName || !form.otherNumber))
    )
  }

  if (tabIndex.value === 2) {
    return (
      !form.lastName ||
      !form.companyCode ||
      !form.companyName ||
      !form.mailbox ||
      !form.officialWebsite ||
      (!form.phone && !form.weChat && !form.whatsApp && (!form.otherName || !form.otherNumber))
    )
  }

  return (
    !form.lastName ||
    !form.TRC20 ||
    !form.nationality ||
    !form.introduction ||
    (!form.email && !form.discord && !form.telegram && (!form.otherName || !form.otherNumber))
  )
})

const rules = computed<FormRules<ContactForm>>(() => ({
  mailbox: [
    { required: true, message: t('83703'), trigger: 'blur' },
    { type: 'email', message: t('83704'), trigger: 'blur' },
  ],
  companyName: [{ required: true, message: t('83703'), trigger: 'blur' }],
  lastName: [{ required: true, message: t('83703'), trigger: ['blur', 'change'] }],
  nationality: [{ required: true, message: t('83703'), trigger: ['blur', 'change'] }],
  TRC20: [{ required: true, message: t('83703'), trigger: ['blur', 'change'] }],
  introduction: [{ required: tabIndex.value === 4, message: t('83703'), trigger: ['blur', 'change'] }],
  officialWebsite: [{ required: true, validator: validateUrl, trigger: 'blur' }],
}))

const formInitialState = (): ContactForm => ({
  companyCode: '',
  companyName: '',
  mailbox: '',
  phone: '',
  weChat: '',
  whatsApp: '',
  otherNumber: '',
  otherName: '',
  officialWebsite: '',
  lastName: '',
  nationality: nationObj.name,
  email: '',
  TRC20: '',
  discord: '',
  telegram: '',
  introduction: '',
})

const itemPhoneRef = ref<HTMLElement>()
const itemEmailRef = ref<HTMLElement>()
const areaTriggerRef = ref<HTMLElement>()
const areaPanelRef = ref<HTMLElement>()
const nationTriggerRef = ref<HTMLElement>()
const nationPanelRef = ref<HTMLElement>()
let tabListResizeObserver: ResizeObserver | null = null
let contactEnterTime = 0
let hasTrackedContactExit = false

const scrollToField = (el?: HTMLElement) => {
  if (!el || isMobile.value) return
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

function updateTabOverflowState() {
  if (!import.meta.client) return
  const el = tabListRef.value
  if (!el) return
  isTabOverflowing.value = el.scrollWidth > el.clientWidth
}

function validateUrl(rule: unknown, value: string, callback: (error?: Error) => void) {
  const reg = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'*+,;=.]+$/
  if (!value) {
    callback(new Error(t('83703')))
    return
  }
  if (!reg.test(value)) {
    callback(new Error(t('83705')))
    return
  }
  callback()
}

function isMobileNum(value: string) {
  return /^1[3-9][0-9]\d{8}$/.test(value)
}

function isInternalMobile(value: string) {
  return /^\d{5,20}$/.test(value)
}

function isEmail(value: string) {
  return /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/.test(value)
}

function phoneValidate() {
  if (!hasContactWay()) {
    errText.value = t('83706')
    isErrText.value = true
    scrollToField(tabIndex.value === 4 ? itemEmailRef.value : itemPhoneRef.value)
    return false
  }

  if (form.phone) {
    const valid = phoneObj.code === '0086' ? isMobileNum(form.phone) : isInternalMobile(form.phone)
    if (!valid) {
      errText.value = t('83707')
      isErrText.value = true
      scrollToField(itemPhoneRef.value)
      return false
    }
  }

  if (form.email && !isEmail(form.email)) {
    errText.value = t('83704')
    isErrText.value = true
    scrollToField(itemEmailRef.value)
    return false
  }

  errText.value = ''
  isErrText.value = false
  return true
}

function hasContactWay() {
  return Boolean(
    form.phone ||
    form.weChat ||
    form.whatsApp ||
    form.email ||
    form.discord ||
    form.telegram ||
    (form.otherNumber && form.otherName),
  )
}

function resolveValidationScroll(fields?: Record<string, any>) {
  if (isMobile.value || !fields) return
  const firstKey = Object.keys(fields)[0]
  const field = fields[firstKey]
  const node = Array.isArray(field) ? field[0] : field
  node?.$el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
}

// 统一将联系方式字段转换为字符串，避免手机号等值被提交为 number 类型。
function toContactWay(value: string | number) {
  return String(value)
}

function getRequestData() {
  const requestObj: Record<string, any> = {}
  if (tabIndex.value !== 4) {
    requestObj.type = tabIndex.value + 1
  }
  requestObj.code = form.companyCode || ''
  requestObj.name = form.companyName
  requestObj.email = form.mailbox
  requestObj.webSite = form.officialWebsite || ''
  requestObj.content = form.introduction
  requestObj.userId = appStore.user?.userId || ''
  requestObj.userName = form.lastName
  requestObj.nationality = form.nationality
  requestObj.TRC20 = form.TRC20
  requestObj.introduction = form.introduction
  requestObj.languageCode = appStore.LanguageCode
  requestObj.countryCode = appStore.CountryCode
  requestObj.contactWays = []

  if (form.weChat) {
    requestObj.contactWays.push({ type: 1, otherTypeName: '', areaCode: '', contactWay: toContactWay(form.weChat) })
  }
  if (form.phone) {
    requestObj.contactWays.push({
      type: 2,
      otherTypeName: '',
      areaCode: phoneObj.code,
      contactWay: toContactWay(form.phone),
    })
  }
  if (form.whatsApp) {
    requestObj.contactWays.push({ type: 3, otherTypeName: '', areaCode: '', contactWay: toContactWay(form.whatsApp) })
  }
  if (form.otherName && form.otherNumber) {
    requestObj.contactWays.push({
      type: 4,
      otherTypeName: form.otherName,
      areaCode: '',
      contactWay: toContactWay(form.otherNumber),
    })
  }
  if (form.email) {
    requestObj.contactWays.push({ type: 1, otherTypeName: '', areaCode: '', contactWay: toContactWay(form.email) })
  }
  if (form.discord) {
    requestObj.contactWays.push({ type: 2, otherTypeName: '', areaCode: '', contactWay: toContactWay(form.discord) })
  }
  if (form.telegram) {
    requestObj.contactWays.push({ type: 3, otherTypeName: '', areaCode: '', contactWay: toContactWay(form.telegram) })
  }

  requestObj.attachments = imgArr.value
  return requestObj
}

function resetForm() {
  formRef.value?.resetFields()
  Object.assign(form, formInitialState())
  imgArr.value = []
  isErrText.value = false
  errText.value = t('83706')
  isUpdateErr.value = false
  updateText.value = t('83708')
  touchIdx.value = 0
}

function changeTab(code: number) {
  matomo.trackEvent('contact_us_tab', {
    af_type: CONTACT_TAB_TRACK_TYPE_MAP[code] || '',
  })
  tabIndex.value = code
  resetForm()
}

function trackContactSubmit(isSuccess: boolean, failureReason = '') {
  matomo.trackEvent('contact_us_submit', {
    af_type: CONTACT_TAB_TRACK_TYPE_MAP[tabIndex.value] || '',
    af_judge: isSuccess ? '是' : '否',
    af_type1: failureReason,
  })
}

function selectPhoneArea(item: AreaCountry) {
  Object.assign(phoneObj, item)
  showAreaCode.value = false
  areaKeyword.value = ''
}

function selectNation(item: AreaCountry) {
  Object.assign(nationObj, item)
  form.nationality = item.name
  showNationCode.value = false
  nationKeyword.value = ''
}

function deleteImgArr(index: number) {
  imgArr.value.splice(index, 1)
}

function uploadPhoto(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  const fileType = file.name.split('.').pop()?.toLowerCase() || ''

  if (file.size > 2621440) {
    isUpdateErr.value = true
    updateText.value = t('83708')
    input.value = ''
    return
  }

  if (!['png', 'jpg', 'jpeg'].includes(fileType)) {
    isUpdateErr.value = true
    updateText.value = t('83709')
    input.value = ''
    return
  }

  isUpdateErr.value = false
  updateText.value = t('83708')
  const reader = new FileReader()
  reader.addEventListener('load', (loadEvent) => {
    const result = loadEvent.target?.result
    if (typeof result === 'string') {
      imgArr.value.push({ base64: result, ext: fileType })
    }
  })
  reader.readAsDataURL(file)
  input.value = ''
}

async function querySearch(queryString: string, cb: (items: CompanySearchSuggestion[]) => void) {
  if (!queryString.trim()) {
    form.companyCode = ''
    cb([])
    return
  }

  const result = await request({
    baseURL: wikibitSearchGateway,
    url: '/api/v4/bit/search/results',
    method: 'GET',
    params: {
      keyword: queryString,
      page: 1,
      size: 20,
    },
  }).catch(() => null)

  const responseData = result?.data || result
  const items = responseData?.list || []
  cb(
    items.map((item: any) => ({
      code: item.code,
      value: item.shortName || item.showName || '',
      shortName: item.shortName || item.showName || '',
      ico: item.ico || item.icon || item.logo || item.hico || '',
      type: item.type,
    })),
  )
}

function handleSelect(item: CompanySearchSuggestion) {
  form.companyName = item.shortName || item.value || ''
  form.companyCode = item.code || ''
}

async function submitForm() {
  if (submitting.value) return

  const valid = await formRef.value?.validate().catch((fields) => {
    resolveValidationScroll(fields)
    phoneValidate()
    return false
  })

  if (!valid) {
    trackContactSubmit(false, t('83703'))
    return
  }
  if (!phoneValidate()) {
    trackContactSubmit(false, errText.value || t('83706'))
    return
  }

  submitting.value = true
  const payload = getRequestData()
  const routeName = tabIndex.value === 4 ? 'contact$kolpost' : 'contact$contact'

  try {
    const result = await invokeV3(routeName, payload).catch(() => null)
    const node = tabIndex.value === 4 ? result?.contact?.kolpost : result?.contact?.contact
    const success = Boolean(node?.success)

    tipObj.type = success ? 1 : 0
    tipObj.content = success ? node?.data || '' : t('83710')
    trackContactSubmit(success, success ? '' : tipObj.content)
    if (success) {
      resetForm()
    }
    dialogVisible.value = true
  } finally {
    submitting.value = false
  }
}

function handleDocumentClick(event: MouseEvent) {
  const target = event.target as Element | null
  const link = target?.closest<HTMLAnchorElement>('a[href]')

  // 新窗口路由不会离开当前 Vue 路由，点击导航链接时按离开页面处理。
  if (link && (link.target === '_blank' || event.button === 1 || event.ctrlKey || event.metaKey || event.shiftKey)) {
    trackContactExit()
  }

  if (
    showAreaCode.value &&
    target &&
    !areaPanelRef.value?.contains(target) &&
    !areaTriggerRef.value?.contains(target)
  ) {
    showAreaCode.value = false
  }
  if (
    showNationCode.value &&
    target &&
    !nationPanelRef.value?.contains(target) &&
    !nationTriggerRef.value?.contains(target)
  ) {
    showNationCode.value = false
  }
}

function trackContactTime(eventTime: 'enter' | 'exit') {
  if (eventTime === 'enter') {
    contactEnterTime = Date.now()
    matomo.trackEvent('contact_us_time', {
      af_Enter: contactEnterTime,
    })
    return
  }

  matomo.trackEvent('contact_us_time', {
    af_Enter: contactEnterTime,
    af_Exit: Date.now(),
  })
}

// 页面卸载与浏览器 pagehide 都可能表示离开，使用标记确保退出事件只上报一次。
function trackContactExit() {
  if (hasTrackedContactExit) {
    return
  }

  hasTrackedContactExit = true
  trackContactTime('exit')
}

// pagehide 可覆盖刷新、关闭标签页等不会触发 Vue 卸载钩子的离开场景。
function handleContactPageHide() {
  trackContactExit()
}

// beforeunload 作为 pagehide 的兼容兜底，覆盖部分浏览器的刷新与关闭行为。
function handleContactPageBeforeUnload() {
  trackContactExit()
}

// 切换浏览器标签页或应用窗口会使当前文档进入 hidden 状态，此时记录离开时间。
function handleContactVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    trackContactExit()
  }
}

// 路由切换开始前上报，避免依赖组件销毁时机导致退出事件遗漏。
onBeforeRouteLeave(() => {
  trackContactExit()
})

watch(
  () => [route.query.type, route.query.tabindex],
  ([type, legacyTabindex]) => {
    tabIndex.value = resolveRouteTabIndex(type, legacyTabindex)
    resetForm()
  },
)

watch(
  () => [
    form.phone,
    form.weChat,
    form.whatsApp,
    form.email,
    form.discord,
    form.telegram,
    form.otherName,
    form.otherNumber,
  ],
  () => {
    if (isErrText.value && errText.value === t('83706') && hasContactWay()) {
      isErrText.value = false
      errText.value = ''
    }
  },
)

onMounted(() => {
  trackContactTime('enter')
  document.addEventListener('click', handleDocumentClick)
  document.addEventListener('auxclick', handleDocumentClick)
  document.addEventListener('visibilitychange', handleContactVisibilityChange)
  window.addEventListener('beforeunload', handleContactPageBeforeUnload)
  window.addEventListener('pagehide', handleContactPageHide)
  updateTabOverflowState()

  if (typeof ResizeObserver !== 'undefined' && tabListRef.value) {
    tabListResizeObserver = new ResizeObserver(() => {
      updateTabOverflowState()
    })
    tabListResizeObserver.observe(tabListRef.value)
  }
})

onBeforeUnmount(() => {
  trackContactExit()
  document.removeEventListener('click', handleDocumentClick)
  document.removeEventListener('auxclick', handleDocumentClick)
  document.removeEventListener('visibilitychange', handleContactVisibilityChange)
  window.removeEventListener('beforeunload', handleContactPageBeforeUnload)
  window.removeEventListener('pagehide', handleContactPageHide)
  tabListResizeObserver?.disconnect()
  tabListResizeObserver = null
})
</script>

<style lang="less">
.contract-view {
  min-height: 100vh;
  min-width: 800px;
  overflow-x: auto;
  background: #f7f7f7;
  color: #3d3d3d;

  .width {
    width: min(1200px, calc(100% - 48px));
    margin: 0 auto;
  }

  .banner {
    background: url('https://resource.tech002.com/images/contract/banner-1200.png') no-repeat center top;
    background-size: cover;
    padding: 58px 0 42px;
    color: #fff;
  }

  .banner-text {
    h1 {
      margin: 0 0 18px;
      font-size: 46px;
      line-height: 1.2;
      font-weight: 600;
    }

    h2 {
      margin: 0;
      font-size: 22px;
      line-height: 1.5;
      font-weight: 400;
    }
  }

  .banner-list {
    margin-top: 64px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;

    .item {
      display: flex;
      align-items: center;
      gap: 16px;
      min-height: 88px;
      padding: 16px 18px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.12);
      backdrop-filter: blur(8px);

      img {
        width: 42px;
        height: 42px;
      }

      .item-text p {
        margin: 0;

        &:first-child {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        &:last-child {
          font-size: 14px;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.82);
        }
      }
    }
  }

  .tab-box {
    background: #fff;
    border-bottom: 1px solid #ececec;
  }

  .tab-list {
    display: flex;
    gap: 24px;
    overflow-x: auto;
  }

  .tab-item {
    position: relative;
    border: 0;
    background: transparent;
    color: #a3a3a3;
    font-size: 21px;
    line-height: 1;
    padding: 18px 0;
    cursor: pointer;
    white-space: nowrap;

    &.active {
      color: #3d3d3d;
      font-weight: 600;
      box-shadow: inset 0 -2px 0 #e66112;
    }
  }

  .content {
    padding: 48px 0 84px;
  }

  .title {
    margin-bottom: 32px;
    font-size: 32px;
    line-height: 1.3;
    font-weight: 600;
  }

  .contract-form {
    padding: 40px;
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(50, 57, 71, 0.05);
  }

  .company-name-box {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
  }

  .other-contact {
    margin-top: 12px;
  }

  .other-contact-title {
    margin: 8px 0 24px;
    font-size: 20px;
    line-height: 1.4;
    font-weight: 600;
  }

  .box {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px 28px;
  }

  .item {
    position: relative;
  }

  .contact-name {
    display: inline-flex;
    align-items: center;
    gap: 10px;

    img {
      width: 20px;
      height: 20px;
    }
  }

  .phone-num-box {
    position: relative;
    display: grid;
    grid-template-columns: 130px 1fr;
    gap: 12px;

    input {
      width: 100%;
      height: 40px;
      border: 1px solid var(--el-border-color);
      border-radius: 4px;
      background: #fff;
      padding: 0 12px;
      outline: none;
    }
  }

  .phone-area,
  .selector-trigger,
  .dialling-code {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 40px;
    border: 1px solid var(--el-border-color);
    border-radius: 4px;
    background: #fff;
    padding: 0 12px;
    cursor: pointer;
    box-sizing: border-box;
  }

  .selector-flag {
    width: 24px;
    height: 16px;
    object-fit: cover;
    border-radius: 2px;
  }

  .selector-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .selector-arrow {
    margin-left: auto;
    color: #999;
    transition: transform 0.2s ease;

    &.open {
      transform: rotate(180deg);
    }
  }

  .selector-panel {
    position: absolute;
    z-index: 30;
    top: calc(100% + 8px);
    left: 0;
    width: 100%;
    max-width: 356px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(50, 57, 71, 0.12);
    overflow: hidden;
  }

  .area-panel {
    width: 356px;
  }

  .selector-search {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;

    input {
      width: 100%;
      height: 40px;
      border: 1px solid #d8d8d8;
      border-radius: 8px;
      padding: 0 12px;
      outline: none;
      box-sizing: border-box;
    }
  }

  .selector-results {
    max-height: 320px;
    overflow-y: auto;
  }

  .selector-group-title {
    position: sticky;
    top: 0;
    padding: 8px 16px;
    background: #f7f7f7;
    color: #777;
    font-size: 13px;
  }

  .selector-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border: 0;
    background: #fff;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: #fafafa;
    }
  }

  .selector-code {
    color: #666;
  }

  .selector-empty {
    padding: 28px 16px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }

  .company-autocomplete-wrap {
    position: relative;
  }

  .company-name-autocomplete {
    width: 100%;
  }

  .autocomplete-icon {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    color: #b0b0b0;
    pointer-events: none;
  }

  .autocomplete-item {
    display: flex;
    align-items: center;
    gap: 10px;

    .icon {
      width: 24px;
      height: 24px;
      border-radius: 999px;
      flex-shrink: 0;
      overflow: hidden;
    }

    .name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .error-text {
    margin-top: 8px;
    color: #f56c6c;
    font-size: 13px;
  }

  .contract-textarea {
    margin-top: 28px;

    .text {
      margin-bottom: 12px;
      font-size: 18px;
      line-height: 1.4;
      font-weight: 600;

      &.is-need::after {
        content: ' *';
        color: #f56c6c;
      }
    }
  }

  .upload-box {
    margin-top: 28px;
  }

  .upload-box-name {
    margin-bottom: 16px;
    font-size: 18px;
    line-height: 1.4;
    font-weight: 600;
  }

  .upload-imgarr {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
  }

  .upload-box-imgbox,
  .file-box {
    width: 104px;
    height: 104px;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    background: #f6f7f9;
  }

  .upload-box-imgbox img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .delete-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    border: 0;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 16px;
    cursor: pointer;
  }

  .file-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #c9cfd8;
    cursor: pointer;

    .camera-icon {
      font-size: 30px;
    }
  }

  .kyc-passin {
    display: none;
  }

  .upload-tip {
    width: 100%;
    color: #8c8c8c;
    font-size: 14px;

    &.updateErr {
      color: #f56c6c;
    }
  }

  .submit-btn {
    margin-top: 36px;
    height: 48px;
    border: 0;
    border-radius: 999px;
    background: linear-gradient(90deg, #ff8a00 0%, #e66112 100%);
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;

    &.disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .touch-list {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }

  .item-liaison {
    border: 1px solid #ececec;
    background: #fff;
    border-radius: 12px;
    padding: 12px 8px;
    text-align: center;

    &.selected {
      border-color: #e66112;
      background: rgba(230, 97, 18, 0.06);
    }
  }

  .ico-liaison {
    width: 22px;
    height: 22px;
    margin-bottom: 8px;
  }

  .liaison-txt {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
  }

  .corporation-box {
    position: relative;
  }

  .input-zone {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 12px;

    .corporation-value {
      width: 100%;
      height: 40px;
      border: 1px solid var(--el-border-color);
      border-radius: 8px;
      padding: 0 12px;
      outline: none;
    }
  }

  .mobile-selector-panel {
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    max-width: none;
  }

  .dialog-title {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title-icon {
    width: 22px;
    height: 22px;
    border-radius: 999px;
    display: inline-block;
  }

  .title-icon-success {
    background: url('https://resource.tech002.com/images/contract/icon-success.png') no-repeat center / cover;
  }

  .title-icon-error {
    background: url('https://resource.tech002.com/images/contract/icon-error.png') no-repeat center / cover;
  }

  .title-text {
    font-size: 20px;
    font-weight: 600;
    color: #3d3d3d;
  }

  .dialog-content {
    color: #7a7a7a;
    font-size: 18px;
    line-height: 1.6;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
  }

  .dialog-button {
    width: 132px;
    height: 38px;
    border: 0;
    border-radius: 6px;
    background: #323947;
    color: #fff;
    cursor: pointer;
  }

  .other-name-item {
    margin-bottom: 18px;
  }
}

.contract-autocomplete-popper {
  .el-autocomplete-suggestion__list li {
    padding: 10px 12px;
  }
}

@media screen and (max-width: 999px) {
  .contract-view {
    .company-name-box,
    .box,
    .banner-list {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

@media screen and (max-width: 799px) {
  .contract-view {
    .width {
      width: 800px;
    }

    min-width: 800px;
  }
}
</style>

<style lang="less">
/* Pixel-aligned port of the legacy contract page. */
.contract-view {
  min-width: 800px;
  overflow-x: auto;
  background-color: #fff;
  padding-bottom: 50px;
  color: #3d3d3d;

  .width {
    width: 1200px;
    max-width: none;
    margin: 0 auto;
    padding: 0;
  }

  .banner {
    display: block;
    width: 100%;
    padding: 0;
    color: inherit;
    background: url('https://resources1.interface003.com/web30/2026/07/10/829a8c46-65cf-4161-8a76-e1a984859174.png')
      no-repeat;
    background-size: cover;

    .banner-text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      padding: 170px 0 93px;

      h1 {
        color: #3d3d3d;
        font-size: 55px;
        font-weight: 600;
        line-height: 77px;
        margin: 0 0 8px;
      }

      h2 {
        color: #636976;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        margin: 0;
      }
    }

    .banner-list {
      display: flex;
      grid-template-columns: none;
      gap: 0;
      margin-top: 0;
      padding: 30px 0;
      background: rgba(255, 255, 255, 0.6);
      border-radius: 10px 10px 0 0;

      .item {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        width: 25%;
        min-height: 0;
        padding: 0 10px;
        gap: 0;
        border-radius: 0;
        background: transparent;
        backdrop-filter: none;

        &::after {
          content: '';
          width: 1px;
          height: 38px;
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          background-color: rgba(0, 0, 0, 0.06);
        }

        &:last-child::after {
          display: none;
        }

        img {
          width: auto;
          height: auto;
          margin-right: 10px;
        }

        .item-text p {
          margin: 0;

          &:first-child {
            margin: 0;
            color: rgba(42, 98, 255, 0.7);
            font-size: 18px;
            font-weight: 400;
            line-height: 25px;
          }

          &:last-child {
            color: rgba(101, 110, 133, 0.5);
            font-size: 14px;
            line-height: 20px;
          }
        }
      }
    }
  }

  .tab-box {
    margin-bottom: 20px;
    border: 0;
    background: #fff;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.05);
    text-align: center;
  }

  .tab-list {
    display: flex;
    justify-content: center;
    gap: 0;
    overflow-x: auto;

    &.is-overflowing {
      justify-content: flex-start;
    }
  }

  .tab-item {
    margin: 0 33px;
    padding: 20px 0;
    color: #a3a3a3;
    font-size: 20px;
    line-height: 33px;
    font-weight: 400;

    &.active {
      color: #3d3d3d;
      font-size: 24px;
      font-weight: 600;
      box-shadow: inset 0 -1px 0 #3d3d3d;
    }
  }

  .content {
    padding: 0;

    .title {
      display: block;
      margin: 0;
      padding: 50px 0;
      color: rgba(61, 61, 61, 0.3);
      font-size: 32px;
      font-weight: 600;
      text-align: center;
    }
  }

  .contract-form {
    padding: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;

    .companyName-box {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 0;
    }

    .el-form-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 540px;
      margin-bottom: 46px;

      .el-form-item__label {
        height: auto;
        padding: 0;
        margin-bottom: 16px;
        color: #3d3d3d;
        font-size: 16px;
        line-height: 19px;

        &::before {
          margin-right: 3px;
          color: #ef6828;
        }
      }

      .el-form-item__content,
      .companyName-autocomplete {
        position: relative;
        width: 100%;
      }

      .el-input__wrapper {
        width: 100%;
        height: 44px;
        padding: 1px 15px;
        border: 1px solid #e6e6e6;
        border-radius: 5px;
        box-shadow: none;
        box-sizing: border-box;

        &.is-focus {
          border-color: #3d3d3d;
          box-shadow: none;
        }
      }

      .el-input__inner {
        height: 42px;
        font-size: 14px;

        &::placeholder {
          color: #ccc;
          font-size: 16px;
        }
      }
    }

    .el-form-item.is-error .el-input__wrapper {
      border-color: #e66112;
      box-shadow: none;
    }

    .el-form-item__error {
      padding-top: 8px;
      color: #e66112;
    }

    .company-autocomplete-wrap,
    .companyName-autocomplete {
      width: 100%;
    }

    .companyName-autocomplete .el-input__wrapper {
      padding-right: 40px;
    }

    .autocomplete-icon {
      right: 16px;
      top: 22px;
      color: #3d3d3d;
      font-size: 18px;
    }

    .nationality-box {
      position: relative;
    }

    .selector-trigger {
      display: flex;
      align-items: center;
      width: 100%;
      height: 44px;
      padding: 0 15px;
      border: 1px solid #e6e6e6;
      border-radius: 5px;
      cursor: pointer;

      .selector-flag {
        width: 28px;
        height: 18px;
        border-radius: 2px;
      }

      .selector-arrow {
        order: 2;
        margin: 0 8px;
      }

      .selector-name {
        order: 3;
        flex: 1;
        color: #606266;
        font-size: 14px;
      }
    }

    .other-contact {
      margin-top: 0;

      .other-contact-title {
        margin: 0 0 46px;
        color: #3d3d3d;
        font-size: 16px;
        font-weight: 600;

        &::before {
          content: '*';
          margin-right: 3px;
          color: #ef6828;
        }
      }

      .box {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0;

        .item {
          position: relative;
          width: 540px;

          .errer {
            position: absolute;
            bottom: 24px;
            left: 0;
            color: #e66112;
            font-size: 12px;
          }

          .name {
            display: flex;
            align-items: center;
            gap: 0;
            // margin-bottom: 16px;

            img {
              width: 24px;
              height: 24px;
              margin-right: 10px;
            }

            span {
              color: #3d3d3d;
              font-size: 16px;
              font-weight: 500;
              line-height: 20px;
            }
          }

          .el-form-item {
            width: 100%;
          }
        }
      }

      .box-h5 {
        display: none;
      }
    }

    .phone-num-box {
      position: relative;
      display: flex;
      align-items: center;
      grid-template-columns: none;
      gap: 0;
      width: 100%;
      padding: 0 16px;
      margin-bottom: 46px;
      border: 1px solid #e6e6e6;
      border-radius: 5px;

      .left {
        display: flex;
        align-items: center;
        width: auto;
        height: auto;
        padding: 0;
        margin-right: 10px;
        border: 0;
        cursor: pointer;

        img {
          width: 28px;
          height: 18px;
        }

        span {
          margin: 0 6px;
          font-size: 18px;
        }
      }

      > input {
        flex: 1;
        width: auto;
        height: auto;
        padding: 0;
        border: 0;
        border-radius: 0;
        color: #606266;
        font-size: 14px;
        line-height: 42px;

        &::placeholder {
          color: #ccc;
          font-size: 16px;
        }
      }
    }

    .other-name-item {
      margin-bottom: 16px !important;
    }

    .contract-textarea {
      margin-top: 0;

      .text {
        margin-bottom: 20px;
        color: #3d3d3d;
        font-size: 16px;
        font-weight: 500;

        &.is-need::after {
          display: none;
        }

        &.is-need::before {
          content: '*';
          margin-right: 3px;
          color: #ef6828;
        }
      }

      .el-form-item {
        width: 100%;
      }

      .el-textarea__inner {
        height: 220px;
        padding: 12px 15px;
        border: 1px solid #e6e8eb;
        border-radius: 5px;
        background: #fafafa;
        box-shadow: none;
        resize: none;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif;

        &:focus {
          border-color: #3d3d3d;
          box-shadow: none;
        }
      }

      .el-input__count {
        right: 20px;
        background: #fafafa;
        color: #ccc;
        font-size: 16px;
      }
    }

    .upload-box {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      margin: 0 0 48px;
      padding: 0 0 46px;
      border-bottom: 1px solid #e6e6e6;

      .upload-box-name {
        margin-bottom: 20px;
        color: #3d3d3d;
        font-size: 16px;
        font-weight: 500;
      }

      .upload-imgarr {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0;

        .text {
          width: auto;
          margin-left: 20px;
          color: #7a7a7a;
          font-size: 14px;

          &.updateErr {
            color: #e66112;
          }
        }
      }

      .upload-box-imgbox {
        position: relative;
        display: flex;
        width: auto;
        height: auto;
        margin-right: 20px;
        border-radius: 0;
        overflow: visible;
        background: transparent;

        img {
          width: 80px;
          height: 80px;
          border-radius: 4px;
          object-fit: cover;
        }

        .delete-btn {
          top: -7px;
          right: -8px;
          width: 17px;
          height: 17px;
          padding: 0;
          border-radius: 50%;
          background: #3d3d3d;
          color: #fff;
          font-size: 14px;
          line-height: 17px;
        }
      }

      .file-box {
        position: relative;
        width: 80px;
        height: 80px;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 4px;
        background: #fafafa;

        .camera-icon {
          color: #979797;
          font-size: 26px;
        }
      }
    }

    .submit-btn {
      display: block;
      height: auto;
      margin: 0 auto;
      padding: 12px 64px;
      border-radius: 26px;
      background: #112d73;
      color: #fff;
      font-size: 14px;
      font-weight: 600;

      &:hover {
        opacity: 0.8;
      }
    }
  }
}

.contract-autocomplete-popper {
  padding: 0 8px;

  .el-autocomplete-suggestion__list li {
    display: flex;
    align-items: center;
    padding: 7px 8px;
  }
}

.contract-dialog {
  padding: 30px 40px;
  border-radius: 12px;

  .el-dialog__header,
  .el-dialog__body,
  .el-dialog__footer {
    padding: 0;
  }

  .dialog-title {
    gap: 11.5px;
  }

  .dialog-content {
    margin: 24px 0 30px;
    color: #7a7a7a;
    font-size: 18px;
    line-height: 25px;
  }
}

[dir='rtl'] .contract-view {
  direction: rtl;

  .banner .banner-list .item {
    &::after {
      right: auto;
      left: 0;
    }

    img {
      margin-right: 0;
      margin-left: 10px;
    }

    &:first-child::after {
      display: none;
    }

    &:last-child::after {
      display: block;
    }
  }

  .tab-list.is-overflowing {
    justify-content: flex-end;
  }

  .contract-form {
    .el-form-item {
      align-items: flex-start;

      .el-form-item__label::before {
        margin-right: 0;
        margin-left: 3px;
      }
    }

    .companyName-autocomplete .el-input__wrapper {
      padding-right: 15px;
      padding-left: 40px;
    }

    .autocomplete-icon {
      right: auto;
      left: 16px;
    }

    .selector-trigger .selector-arrow {
      margin-right: 8px;
      margin-left: 8px;
    }

    .other-contact {
      .other-contact-title::before {
        margin-right: 0;
        margin-left: 3px;
      }

      .box .item {
        .errer {
          right: 0;
          left: auto;
        }

        .name img {
          margin-right: 0;
          margin-left: 10px;
        }
      }
    }

    .phone-num-box {
      .left {
        margin-right: 0;
        margin-left: 10px;
      }

      > input {
        text-align: right;
      }
    }

    .contract-textarea {
      .text.is-need::before {
        margin-right: 0;
        margin-left: 3px;
      }

      .el-input__count {
        right: auto;
        left: 20px;
      }
    }

    .upload-box {
      .upload-imgarr .text {
        margin-right: 20px;
        margin-left: 0;
      }

      .upload-box-imgbox {
        margin-right: 0;
        margin-left: 20px;

        .delete-btn {
          right: auto;
          left: -8px;
        }
      }
    }
  }

  .selector-arrow {
    margin-right: auto;
    margin-left: 0;
  }

  .selector-panel {
    right: 0;
    left: auto;
    text-align: right;
  }

  .selector-option {
    text-align: right;
  }

  .mobile-selector-panel {
    right: 0;
    left: 0;
  }
}

[dir='rtl'] .contract-autocomplete-popper {
  direction: rtl;

  .autocomplete-item {
    direction: rtl;
  }
}

[dir='rtl'] .contract-dialog {
  direction: rtl;

  .dialog-footer {
    justify-content: flex-start;
  }
}

@media screen and (max-width: 1199px) {
  .contract-view {
    .width {
      width: 940px;
    }

    .banner .banner-list {
      .item {
        width: 33.33%;
      }

      .item:nth-child(3)::after {
        display: none;
      }

      .item:last-child {
        display: none;
      }
    }

    .contract-form {
      .el-form-item,
      .other-contact .box .item {
        width: 445px;
      }
    }
  }
}

@media screen and (max-width: 999px) {
  .contract-view {
    .width {
      width: 800px;
    }

    .contract-form {
      .el-form-item,
      .other-contact .box .item {
        width: 355px;
      }
    }
  }
}

@media screen and (max-width: 799px) {
  .contract-view {
    .width {
      width: 800px;
    }

    min-width: 800px;
  }
}
</style>
